import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch (err) {
      // 401 on /auth/me is the expected "no active session" state — do not log noisy errors.
      if (process.env.NODE_ENV === "development" && err?.response?.status !== 401) {
        console.warn("auth refresh failed", err);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // The one success path for every way of signing in. /auth/login,
  // /auth/register and /auth/google all return {user, joined_teams} with the
  // session already set as cookies, so they all land here.
  const acceptSession = (data) => {
    if (data.joined_teams?.length) localStorage.setItem("welcomeTeams", JSON.stringify(data.joined_teams));
    setUser(data.user);
    return data.user;
  };

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    return acceptSession(r.data);
  };
  const register = async (email, password, name, inviteToken) => {
    // invite_token is required by the backend only while waitlist mode is
    // on; sending it unconditionally is harmless and keeps callers simple.
    const r = await api.post("/auth/register", { email, password, name, invite_token: inviteToken || null });
    return acceptSession(r.data);
  };
  // `credential` is the Google-signed ID token from Google Identity Services;
  // the backend verifies it and issues our own session.
  const loginWithGoogle = async (credential) => {
    const r = await api.post("/auth/google", { credential });
    return acceptSession(r.data);
  };
  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Best-effort — even if the server is down, we still clear local state.
      if (process.env.NODE_ENV === "development") console.warn("logout endpoint failed", err);
    }
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, loginWithGoogle, logout, refresh, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
